import { Component, inject, Input, OnChanges, OnInit, signal, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardsComponent } from '../../shared/cards/cards.component';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../../core/services/api.service';
import { TableHeader } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);

@Component({
  standalone: true,
  selector: 'app-business-state-stats',
  imports: [CommonModule, CardsComponent],
  templateUrl: './business-state-stats.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './business-state-stats.component.scss'
})
export class BusinessStateStatsComponent implements OnInit, OnChanges {
  @Input() dateRange!: DateRangePickerOutput;
  @Input() productServerName: string = 'denefits';
  @Input() requiresAccessToken: boolean = false;
  @Input() cocuId: number = 1;
  @Input() countryId: number = 1;
  /**
   * Optional filter that limits which of the four state cards
   * (`activeBusiness` | `inactiveBusiness` | `activatedBusiness` | `revivedBusiness`)
   * this instance renders and fetches.
   *
   * - When omitted (or empty) → behaves exactly as before, rendering and
   *   fetching all four cards in the internal 2-col grid (default UI
   *   preserved).
   * - When provided → only the listed cards are rendered and only their
   *   matching APIs are called, allowing the parent dashboard to host each
   *   sub-card in its own customizable grid slot.
   */
  @Input() onlyKeys?: string[];

  /** Cards filtered by `onlyKeys` (or all cards if no filter provided). */
  get visibleBusinessCards() {
    const filter = this.onlyKeys;
    if (!filter || !filter.length) return this.businessCards;
    return this.businessCards.filter((c) => filter.includes(c.key));
  }

  /** Maps internal `businessCards.key` → backend `type` slug used by the API. */
  private readonly KEY_TO_TYPE: Record<string, string> = {
    activeBusiness: 'active_business',
    inactiveBusiness: 'inactive_business',
    activatedBusiness: 'activated_business',
    revivedBusiness: 'revived_business',
  };

  businessCards: Array<{
    heading: string;
    shortHeading: string;
    name: string;
    key: string;
    viewIndex: number;
    cardIndex: number;
    primaryLineColor: string;
    valueKey: string;
    allTimeKey?: string;
    allTimeHeading?: string;
    tableHeaders: TableHeader[];
  }> = [
    {
      heading: 'ACTIVE BUSINESS',
      name: 'Active Business',
      shortHeading: 'ACTIVE',
      key: 'activeBusiness',
      viewIndex: 0,
      cardIndex: 0,
      primaryLineColor: '#3DE195',
      valueKey: 'value',
      allTimeKey: 'alltime_active_doctors',
      allTimeHeading: 'All time active doctors',
      tableHeaders: this.getDefaultTableHeaders(),
    },
    {
      heading: 'INACTIVE BUSINESS',
      shortHeading: 'INACTIVE',
      name: 'Inactive Business',
      key: 'inactiveBusiness',
      viewIndex: 1,
      cardIndex: 1,
      primaryLineColor: '#131921',
      valueKey: 'value',
      allTimeKey: 'alltime_inactive_doctors',
      allTimeHeading: 'All time inactive doctors',
      tableHeaders: this.getDefaultTableHeaders(),
    },
    {
      heading: 'BUSINESS ACTIVATED',
      shortHeading: 'ACTIVATED',
      name: 'Activated Business',
      key: 'activatedBusiness',
      viewIndex: 2,
      cardIndex: 5,
      primaryLineColor: '#0C66E8',
      valueKey: 'value',
      tableHeaders: this.getDefaultTableHeaders(),
    },
    {
      heading: 'BUSINESS REVIVED',
      shortHeading: 'REVIVED',
      name: 'Revived Business',
      key: 'revivedBusiness',
      viewIndex: 3,
      cardIndex: 4,
      primaryLineColor: '#FFBF47',
      valueKey: 'value',
      tableHeaders: this.getDefaultTableHeaders(),
    },
  ];

  // Data containers for each business card
  businessData: any = {
    activeBusiness: null,
    inactiveBusiness: null,
    activatedBusiness: null,
    revivedBusiness: null,
  };

  constructor(private apiService: ApiService) { }

  ngOnInit(): void {
    // this.buildBusinessData();
    this.updateAllTableHeaders();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['dateRange'] && changes['dateRange'].currentValue) || 
        (changes['productServerName'] && !changes['productServerName'].firstChange) ||
        (changes['cocuId'] && !changes['cocuId'].firstChange) ||
        (changes['countryId'] && !changes['countryId'].firstChange)) {
      // Update table headers when dateRange changes
      this.updateAllTableHeaders();
      
      const hasPreviousDates = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date;
      
      if (hasPreviousDates) {
        this.fetchAllData(false, true, true, false);
        this.fetchAllData(false, false, false, true);
      }
      else {
        this.fetchAllData();
      }
    }
  }

  private getDefaultTableHeaders() {
    return [
      { label: 'Period', key: 'start', align: 'text-left' as const, noTransform: true },
      { label: 'Total', key: 'total', align: 'text-center' as const },
      { label: 'Dental', key: 'dental', align: 'text-center' as const },
      { label: 'Healthcare', key: 'healthcare', align: 'text-center' as const },
      { label: 'Non-Healthcare', key: 'non_healthcare', align: 'text-center' as const },
    ];
  }

  private updateAllTableHeaders(): void {
    const unit = this.dateRange?.unit || 'day';
    const dateLabel = this.getDateLabel(unit);
     
    // Format date ranges for current and previous periods using unit-aware formatting
    const currentPeriodLabel = this.dateRange?.start_date && this.dateRange?.end_date
      ? this.formatDateRangeByUnit(this.dateRange.start_date, this.dateRange.end_date, unit)
      : '';
    
    const previousPeriodLabel = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date
      ? this.formatDateRangeByUnit(this.dateRange.previous_start_date, this.dateRange.previous_end_date, unit)
      : '';
    
    // Update table headers for all business cards
    this.businessCards.forEach(card => {
      const headers: TableHeader[] = [
        { label: dateLabel, key: 'start', align: 'text-left', noTransform: true },
        { label: `Current${currentPeriodLabel ? ` (${currentPeriodLabel})` : ''}`, key: 'total', align: 'text-center' },
        { label: `Previous${previousPeriodLabel ? ` (${previousPeriodLabel})` : ''}`, key: 'total', align: 'text-center', isPrevious: true },
      ];
      card.tableHeaders = headers;
    });
  }

  private getDateLabel(unit: string): string {
    const labels: { [key: string]: string } = {
      'hour': 'Hour',
      'day': 'Date',
      'week': 'Week',
      'month': 'Month',
      'quarter': 'Quarter',
      'year': 'Year'
    };
    return labels[unit] || 'Date';
  }

  private formatDateRangeByUnit(startDate: string, endDate: string, unit: string): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    switch (unit) {
      case 'hour':
        // For hourly: show date with hour range
        if (start.isSame(end, 'day')) {
          return `${start.format('DD MMM')} (${start.format('HH:mm')} - ${end.format('HH:mm')})`;
        }
        return `${start.format('DD MMM HH:mm')} - ${end.format('DD MMM HH:mm')}`;
      
      case 'day':
        // For daily: show date range
        if (start.isSame(end, 'month')) {
          return `${start.format('DD')} - ${end.format('DD MMM YYYY')}`;
        }
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      
      case 'week':
        // For weekly: show date range
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      
      case 'month':
        // For monthly: show month range
        if (start.isSame(end, 'year')) {
          return `${start.format('MMM')} - ${end.format('MMM YYYY')}`;
        }
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
      
      case 'quarter':
        // For quarterly: show quarter range
        return `Q${start.quarter()} ${start.format('YYYY')} - Q${end.quarter()} ${end.format('YYYY')}`;
      
      case 'year':
        // For yearly: show year range
        if (start.year() === end.year()) {
          return start.format('YYYY');
        }
        return `${start.format('YYYY')} - ${end.format('YYYY')}`;
      
      default:
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
    }
  }

  private formatStartDate(startDate: string, endDate: string, unit: string): string {
    if (unit === 'week') {
      return `${dayjs(startDate).format('DD MMM')} - ${dayjs(endDate).format('DD MMM')}`;
    } else if (unit === 'day') {
      return `${dayjs(startDate).format('DD MMM')}`;
    } else if (unit === 'quarter') {
      return `${dayjs(startDate).format('MMM YY')} - ${dayjs(endDate).format('MMM YY')}`;
    } else if (unit === 'year') {
      return `${dayjs(startDate).format('YYYY')}`;
    } else if (unit === 'month') {
      return `${dayjs(startDate).format('MMM YYYY')}`;
    } else if (unit === 'hour') {
      return `${dayjs(startDate).format('DD MMM HH:mm')}`;
    }
    return dayjs(startDate).format('DD MMM');
  }

  // buildBusinessData(): void {
  //   const unit = this.dateRange?.unit || 'day';
    
  //   // Build active business data - keep original dates for chart
  //   const activeChartData = activeBusinessesData.records.map(item => ({
  //     start: item.start,
  //     end: item.end,
  //     value: item.total,
  //   }));

  //   const activeTableData = activeBusinessesData.records.map(item => ({
  //     ...item,
  //     start: this.formatStartDate(item.start, item.end, unit)
  //   }));

  //   this.businessData.activeBusiness = {
  //     chartData: activeChartData,
  //     previousChartData: [],
  //     tableData: activeTableData,
  //     previousTableData: [],
  //     summary: {
  //       current: activeBusinessesData.stats.total,
  //       previous: null,
  //       allTime: activeBusinessesData.stats.alltime_active_doctors
  //     }
  //   };

  //   // Build inactive business data - keep original dates for chart
  //   const inactiveChartData = inactiveBusinessesData.records.map(item => ({
  //     start: item.start,
  //     end: item.end,
  //     value: item.total,
  //   }));

  //   const inactiveTableData = inactiveBusinessesData.records.map(item => ({
  //     ...item,
  //     start: this.formatStartDate(item.start, item.end, unit)
  //   }));

  //   this.businessData.inactiveBusiness = {
  //     chartData: inactiveChartData,
  //     previousChartData: [],
  //     tableData: inactiveTableData,
  //     previousTableData: [],
  //     summary: {
  //       current: inactiveBusinessesData.stats.total,
  //       previous: null,
  //       allTime: inactiveBusinessesData.stats.alltime_inactive_doctors
  //     }
  //   };

  //   // Build activated business data - keep original dates for chart
  //   const activatedChartData = activatedBusinessesData.records.map(item => ({
  //     start: item.start,
  //     end: item.end,
  //     value: item.total,
  //   }));

  //   const activatedTableData = activatedBusinessesData.records.map(item => ({
  //     ...item,
  //     start: this.formatStartDate(item.start, item.end, unit)
  //   }));

  //   this.businessData.activatedBusiness = {
  //     chartData: activatedChartData,
  //     previousChartData: [],
  //     tableData: activatedTableData,
  //     previousTableData: [],
  //     summary: {
  //       current: activatedBusinessesData.stats.total,
  //       previous: null,
  //       allTime: null
  //     }
  //   };

  //   // Build revived business data - keep original dates for chart
  //   const revivedChartData = revivedBusinessesData.records.map(item => ({
  //     start: item.start,
  //     end: item.end,
  //     value: item.total,
  //   }));

  //   const revivedTableData = revivedBusinessesData.records.map(item => ({
  //     ...item,
  //     start: this.formatStartDate(item.start, item.end, unit)
  //   }));

  //   this.businessData.revivedBusiness = {
  //     chartData: revivedChartData,
  //     previousChartData: [],
  //     tableData: revivedTableData,
  //     previousTableData: [],
  //     summary: {
  //       current: revivedBusinessesData.stats.total,
  //       previous: null,
  //       allTime: null
  //     }
  //   };
  // }

    private fetchAllData(
    useCache: boolean = false,
    useLoader: boolean = false,
    createMainChart: boolean = true,
    isPrevious: boolean = false
  ) {
    const allTypes = [
      'active_business',
      'inactive_business',
      'activated_business',
      'revived_business',
    ];
    // Restrict API calls to only the visible card types when the parent
    // has filtered this instance via `onlyKeys`. Without `onlyKeys`, all
    // four endpoints are called (existing behaviour).
    const allowedTypes = (this.onlyKeys && this.onlyKeys.length)
      ? allTypes.filter((t) =>
          this.onlyKeys!.some((k) => this.KEY_TO_TYPE[k] === t),
        )
      : allTypes;
    allowedTypes.forEach((type) =>
      this.FetchActiveBusinessData(
        type,
        useCache,
        false,
        createMainChart,
        isPrevious
      )
    );
  }
isLoading = signal(false)
  FetchActiveBusinessData(
    type: string,
    useCache?: boolean,
    useLoader: boolean = false,
    createMainChart: boolean = true,
    isPrevious: boolean = false
  ) {
    // Find the matching business card configuration
    this.isLoading.set(true)
    const cardData = this.businessCards.find((item: any) => 
      type === 'active_business' && item.key === 'activeBusiness' ||
      type === 'inactive_business' && item.key === 'inactiveBusiness' ||
      type === 'activated_business' && item.key === 'activatedBusiness' ||
      type === 'revived_business' && item.key === 'revivedBusiness'
    );


    if (!cardData) {
      this.isLoading.set(false)
      console.error(`No card configuration found for type: ${type}`);
      return;
    }

    const key = cardData.key;

    const payload: any = {
      start_time: isPrevious
        ? this.dateRange.previous_start_date
        : this.dateRange.start_date || new Date(),
      end_time: isPrevious
        ? this.dateRange.previous_end_date
        : this.dateRange.end_date || new Date(),
      is_dummy: 0,
      // timezone: 'PDT',
      unit: this.dateRange?.unit || 'day',
      outputFormat: 'db',
      stage: 1,
      cocu_id: this.cocuId,
      country_id: this.countryId
    };

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }

    this.apiService
      .post(`/visionboard/stats/${type}`, payload, {
        addAccessToken: this.requiresAccessToken,
        productServerName: this.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          if (response?.is_error === 0 && response?.data) {
           
            this.isLoading.set(false)
            const unit = this.dateRange?.unit || 'day';
            
            // Transform records to chart format - keep original dates for proper chart rendering
            const chartData = (response.data.records || []).map((item: any) => ({
              start: item.start,
              end: item.end,
              value: item.total,
              // key:cardData.heading
            }));


           

            // Transform records to table format with formatted dates
            const tableData = (response.data.records || []).map((item: any) => ({
              ...item,
              start: this.formatStartDate(item.start, item.end, unit)
            }));

            // Check if we have previous dates in dateRange
            const hasPreviousDates = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date;

            // Update businessData based on whether this is previous period data
            if (isPrevious) {
          
              // Update previous period data
              if (this.businessData[key]) {
                this.businessData[key].previousChartData =(chartData || []).map((item: any) => ({
                  ...item,
                  key: 'Previous Businesses'
                })); 
                this.businessData[key].previousTableData = tableData;
                if (this.businessData[key].summary) {
                  this.businessData[key].summary.previous = response.data.stats?.total || 0;
                }
              }
            } else {
              // Update current period data
              this.isLoading.set(false)
              if (!this.businessData[key]) {
                this.businessData[key] = {
                  chartData: [],
                  previousChartData: [],
                  tableData: [],
                  previousTableData: [],
                  summary: {
                    current: 0,
                    previous: undefined,
                    allTime: null
                  }
                };
              }

              this.businessData[key].chartData = (chartData || []).map((item: any) => ({
                ...item,
                key: cardData.name,
              }));
              this.businessData[key].tableData = tableData;
              this.businessData[key].summary.current = response.data.stats?.total || 0;
              
              // Handle previous data based on whether we're in comparison mode
              if (!hasPreviousDates) {
                // Not in comparison mode - clear previous data
                this.businessData[key].summary.previous = undefined;
                this.businessData[key].previousChartData = [];
                this.businessData[key].previousTableData = [];
              }
              // If in comparison mode, preserve existing previous value (will be updated by subsequent isPrevious=true fetch)
              
              // Update allTime values if available in the response
              if (cardData.allTimeKey) {
                this.businessData[key].summary.allTime = response.data.stats[cardData.allTimeKey];
                this.businessData[key].allTimeHeading = cardData.allTimeHeading || '';
              }
            }
          }
        },
        error: (error: any) => {
          console.error(`Error fetching ${type}:`, error);
          this.isLoading.set(false)
        }
      });
  }

}
